import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Plus, Search, Edit, Trash2, Building2, ChevronLeft, ChevronRight, X, 
  Mail, Phone, Eye, CheckCircle, XCircle, Filter 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import api from '@/services/api';
import { PermissionGate } from '@/components/PermissionGate';
import { useToast } from '@/hooks/useToast';
import ServiceProviderForm from '../components/ServiceProviderForm';

export type ProviderType = 'REPAIR_CENTER' | 'IT_SUPPORT' | 'CONSULTANT' | 'CONTRACTOR';

export interface ServiceProvider {
  id: string;
  name: string;
  type: ProviderType;
  contactPerson?: string;
  email?: string;
  phone?: string;
  alternatePhone?: string;
  address?: string;
  city?: string;
  country?: string;
  servicesOffered?: string[];
  specializations?: string[];
  contractStart?: string;
  contractEnd?: string;
  contractValue?: number;
  paymentTerms?: string;
  bankName?: string;
  bankAccount?: string;
  taxId?: string;
  notes?: string;
  isActive: boolean;
  isPrequalified: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface ServiceProviderFormData {
  name: string;
  type: ProviderType;
  contactPerson: string;
  email: string;
  phone: string;
  alternatePhone: string;
  address: string;
  city: string;
  country: string;
  servicesOffered: string;
  specializations: string;
  contractStart: string;
  contractEnd: string;
  contractValue: string;
  paymentTerms: string;
  bankName: string;
  bankAccount: string;
  taxId: string;
  notes: string;
  isActive: boolean;
  isPrequalified: boolean;
}

export const initialFormData: ServiceProviderFormData = {
  name: '',
  type: 'REPAIR_CENTER',
  contactPerson: '',
  email: '',
  phone: '',
  alternatePhone: '',
  address: '',
  city: '',
  country: '',
  servicesOffered: '',
  specializations: '',
  contractStart: '',
  contractEnd: '',
  contractValue: '',
  paymentTerms: '',
  bankName: '',
  bankAccount: '',
  taxId: '',
  notes: '',
  isActive: true,
  isPrequalified: false,
};

const PROVIDER_TYPES: { value: ProviderType; label: string }[] = [
  { value: 'REPAIR_CENTER', label: 'Repair Center' },
  { value: 'IT_SUPPORT', label: 'IT Support' },
  { value: 'CONSULTANT', label: 'Consultant' },
  { value: 'CONTRACTOR', label: 'Contractor' },
];

const getTypeColor = (type: ProviderType) => {
  switch (type) {
    case 'REPAIR_CENTER':
      return 'bg-orange-100 text-orange-800';
    case 'IT_SUPPORT':
      return 'bg-blue-100 text-blue-800';
    case 'CONSULTANT':
      return 'bg-purple-100 text-purple-800';
    case 'CONTRACTOR':
      return 'bg-green-100 text-green-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const getTypeLabel = (type: ProviderType) => {
  return PROVIDER_TYPES.find(t => t.value === type)?.label || type;
};

export default function ServiceProvidersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState<string>('');
  const [filterActive, setFilterActive] = useState<string>('');
  const [filterPrequalified, setFilterPrequalified] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProvider, setEditingProvider] = useState<ServiceProvider | null>(null);
  const [formData, setFormData] = useState<ServiceProviderFormData>(initialFormData);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Fetch providers
  const { data, isLoading } = useQuery({
    queryKey: ['service-providers', { page, search, filterType, filterActive, filterPrequalified }],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', '20');
      if (search) params.append('search', search);
      if (filterType) params.append('type', filterType);
      if (filterActive) params.append('isActive', filterActive);
      if (filterPrequalified) params.append('isPrequalified', filterPrequalified);
      const response = await api.get(`/service-providers?${params.toString()}`);
      return response.data;
    },
  });

  const providers: ServiceProvider[] = data?.data || [];
  const pagination = data?.pagination;

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: ServiceProviderFormData) => {
      const payload = {
        name: data.name,
        type: data.type,
        contactPerson: data.contactPerson || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        alternatePhone: data.alternatePhone || undefined,
        address: data.address || undefined,
        city: data.city || undefined,
        country: data.country || undefined,
        servicesOffered: data.servicesOffered ? data.servicesOffered.split(',').map(s => s.trim()) : undefined,
        specializations: data.specializations ? data.specializations.split(',').map(s => s.trim()) : undefined,
        contractStart: data.contractStart || undefined,
        contractEnd: data.contractEnd || undefined,
        contractValue: data.contractValue ? parseFloat(data.contractValue) : undefined,
        paymentTerms: data.paymentTerms || undefined,
        bankName: data.bankName || undefined,
        bankAccount: data.bankAccount || undefined,
        taxId: data.taxId || undefined,
        notes: data.notes || undefined,
        isActive: data.isActive,
        isPrequalified: data.isPrequalified,
      };
      return api.post('/service-providers', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
      closeModal();
      toast({ title: 'Success', description: 'Service provider created successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create service provider', variant: 'destructive' });
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: ServiceProviderFormData }) => {
      const payload = {
        name: data.name,
        type: data.type,
        contactPerson: data.contactPerson || undefined,
        email: data.email || undefined,
        phone: data.phone || undefined,
        alternatePhone: data.alternatePhone || undefined,
        address: data.address || undefined,
        city: data.city || undefined,
        country: data.country || undefined,
        servicesOffered: data.servicesOffered ? data.servicesOffered.split(',').map(s => s.trim()) : undefined,
        specializations: data.specializations ? data.specializations.split(',').map(s => s.trim()) : undefined,
        contractStart: data.contractStart || undefined,
        contractEnd: data.contractEnd || undefined,
        contractValue: data.contractValue ? parseFloat(data.contractValue) : undefined,
        paymentTerms: data.paymentTerms || undefined,
        bankName: data.bankName || undefined,
        bankAccount: data.bankAccount || undefined,
        taxId: data.taxId || undefined,
        notes: data.notes || undefined,
        isActive: data.isActive,
        isPrequalified: data.isPrequalified,
      };
      return api.put(`/service-providers/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
      closeModal();
      toast({ title: 'Success', description: 'Service provider updated successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to update service provider', variant: 'destructive' });
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/service-providers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['service-providers'] });
      setShowDeleteConfirm(false);
      setDeletingId(null);
      toast({ title: 'Success', description: 'Service provider deleted successfully' });
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to delete service provider', variant: 'destructive' });
    },
  });

  const openCreateModal = () => {
    setEditingProvider(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  const openEditModal = (provider: ServiceProvider) => {
    setEditingProvider(provider);
    setFormData({
      name: provider.name,
      type: provider.type,
      contactPerson: provider.contactPerson || '',
      email: provider.email || '',
      phone: provider.phone || '',
      alternatePhone: provider.alternatePhone || '',
      address: provider.address || '',
      city: provider.city || '',
      country: provider.country || '',
      servicesOffered: provider.servicesOffered?.join(', ') || '',
      specializations: provider.specializations?.join(', ') || '',
      contractStart: provider.contractStart ? provider.contractStart.split('T')[0] : '',
      contractEnd: provider.contractEnd ? provider.contractEnd.split('T')[0] : '',
      contractValue: provider.contractValue?.toString() || '',
      paymentTerms: provider.paymentTerms || '',
      bankName: provider.bankName || '',
      bankAccount: provider.bankAccount || '',
      taxId: provider.taxId || '',
      notes: provider.notes || '',
      isActive: provider.isActive,
      isPrequalified: provider.isPrequalified,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProvider(null);
    setFormData(initialFormData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProvider) {
      updateMutation.mutate({ id: editingProvider.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const confirmDelete = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };

  const clearFilters = () => {
    setFilterType('');
    setFilterActive('');
    setFilterPrequalified('');
    setPage(1);
  };

  const hasActiveFilters = filterType || filterActive || filterPrequalified;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Service Providers</h1>
          <p className="text-muted-foreground">Manage repair centers, IT vendors, consultants and contractors</p>
        </div>
        <PermissionGate permission="vendors.create">
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            Add Provider
          </Button>
        </PermissionGate>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search providers..."
                  className="pl-10"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <Button
                variant={showFilters ? 'default' : 'outline'}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 bg-primary-foreground text-primary rounded-full w-5 h-5 text-xs flex items-center justify-center">
                    !
                  </span>
                )}
              </Button>
            </div>

            {/* Filter Row */}
            {showFilters && (
              <div className="flex flex-wrap gap-4 pt-2 border-t">
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Type</label>
                  <select
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={filterType}
                    onChange={(e) => {
                      setFilterType(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Types</option>
                    {PROVIDER_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Status</label>
                  <select
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={filterActive}
                    onChange={(e) => {
                      setFilterActive(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-sm font-medium">Prequalified</label>
                  <select
                    className="h-10 px-3 rounded-md border border-input bg-background text-sm"
                    value={filterPrequalified}
                    onChange={(e) => {
                      setFilterPrequalified(e.target.value);
                      setPage(1);
                    }}
                  >
                    <option value="">All</option>
                    <option value="true">Prequalified</option>
                    <option value="false">Not Prequalified</option>
                  </select>
                </div>
                {hasActiveFilters && (
                  <div className="flex items-end">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Clear Filters
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : providers.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No service providers found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-4 font-medium">Name</th>
                    <th className="text-left p-4 font-medium">Type</th>
                    <th className="text-left p-4 font-medium">Contact</th>
                    <th className="text-left p-4 font-medium">Location</th>
                    <th className="text-center p-4 font-medium">Status</th>
                    <th className="text-right p-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {providers.map((provider) => (
                    <tr key={provider.id} className="hover:bg-muted/50">
                      <td className="p-4">
                        <div>
                          <div className="font-medium">{provider.name}</div>
                          {provider.contactPerson && (
                            <div className="text-sm text-muted-foreground">{provider.contactPerson}</div>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(provider.type)}`}>
                          {getTypeLabel(provider.type)}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="space-y-1">
                          {provider.email && (
                            <a
                              href={`mailto:${provider.email}`}
                              className="flex items-center gap-1 text-sm text-primary hover:underline"
                            >
                              <Mail className="w-3 h-3" />
                              {provider.email}
                            </a>
                          )}
                          {provider.phone && (
                            <a
                              href={`tel:${provider.phone}`}
                              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                            >
                              <Phone className="w-3 h-3" />
                              {provider.phone}
                            </a>
                          )}
                          {!provider.email && !provider.phone && (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {[provider.city, provider.country].filter(Boolean).join(', ') || '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center gap-2">
                          {provider.isActive ? (
                            <span className="flex items-center gap-1 text-green-600 text-sm">
                              <CheckCircle className="w-4 h-4" />
                              Active
                            </span>
                          ) : (
                            <span className="flex items-center gap-1 text-gray-500 text-sm">
                              <XCircle className="w-4 h-4" />
                              Inactive
                            </span>
                          )}
                          {provider.isPrequalified && (
                            <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                              Prequalified
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => navigate(`/service-providers/${provider.id}`)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <PermissionGate permission="vendors.update">
                            <Button variant="ghost" size="icon" onClick={() => openEditModal(provider)}>
                              <Edit className="w-4 h-4" />
                            </Button>
                          </PermissionGate>
                          <PermissionGate permission="vendors.delete">
                            <Button variant="ghost" size="icon" onClick={() => confirmDelete(provider.id)}>
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" disabled={page === 1} onClick={() => setPage(page - 1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm">Page {page} of {pagination.totalPages}</span>
              <Button variant="outline" size="icon" disabled={page === pagination.totalPages} onClick={() => setPage(page + 1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      {showModal && (
        <ServiceProviderForm
          isOpen={showModal}
          onClose={closeModal}
          onSubmit={handleSubmit}
          formData={formData}
          setFormData={setFormData}
          isEditing={!!editingProvider}
          isLoading={createMutation.isPending || updateMutation.isPending}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold mb-2">Confirm Deletion</h3>
              <p className="text-muted-foreground mb-6">
                Are you sure you want to delete this service provider? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => deletingId && deleteMutation.mutate(deletingId)}
                  loading={deleteMutation.isPending}
                >
                  Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
